/**
 * components/hire/reasonModal.tsx
 * Forces a non-empty reason for reject / withdraw (the API returns 422 without
 * one). Shared by the match results table and the pipeline kanban.
 */
import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton } from '../ui';
import { web } from '../../utils/web';

export default function ReasonModal({
  open, title, onClose, onSubmit,
}: { open: boolean; title: string; onClose: () => void; onSubmit: (reason: string) => Promise<void> | void }) {
  const { theme } = useTheme();
  const { colors, fontSize, radii } = theme;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) { setReason(''); onClose(); } };
  const submit = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await onSubmit(reason.trim()); setReason(''); } finally { setBusy(false); }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      size="sm"
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton label="Confirm" variant="danger" onPress={submit} loading={busy} disabled={!reason.trim()} />
        </View>
      }
    >
      <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginBottom: 8 }}>A reason is required and recorded on the append-only action stream.</Text>
      <TextInput
        value={reason}
        onChangeText={setReason}
        placeholder="Reason…"
        placeholderTextColor={colors.ink4}
        multiline
        style={[{ minHeight: 80, padding: 11, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, backgroundColor: colors.card, color: colors.ink, fontSize: fontSize.md, textAlignVertical: 'top' }, web({ outline: 'none' })]}
      />
    </Modal>
  );
}
